import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Avatar,
  IconButton,
  Spinner,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiTrash2, FiSearch, FiUserPlus } from 'react-icons/fi'
import { useAppDispatch, useAppSelector } from '@/store'
import { useAppTranslation } from '@/contexts/LanguageContext'
import {
  fetchGroupMembers,
  addGroupMember,
  removeGroupMember,
} from '@/store/slices/groupsSlice'
import apiService from '@/services/api'
import { Group, GroupMember } from '@/types'

/** Stable fallback so useSelector does not see a new [] reference when data is missing. */
const EMPTY_GROUP_MEMBERS: GroupMember[] = []

interface OrgUser {
  id: number
  name: string
  email: string
  role: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  group: Group
}

export default function GroupMembersModal({ isOpen, onClose, group }: Props) {
  const { t } = useAppTranslation(['admin', 'common'])
  const dispatch = useAppDispatch()
  const toast = useToast()

  const members = useAppSelector(
    (state) => state.groups.membersById[group.id] ?? EMPTY_GROUP_MEMBERS
  )
  const isLoading = useAppSelector((state) => state.groups.membersLoading[group.id] || false)

  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [isAdding, setIsAdding] = useState<number | null>(null)

  const rowBg = useColorModeValue('gray.50', 'gray.700')

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchGroupMembers(group.id))
      loadOrgUsers()
    } else {
      setSearch('')
    }
  }, [isOpen, group.id, dispatch])

  const loadOrgUsers = async () => {
    setUsersLoading(true)
    try {
      const users = await apiService.getOrganizationUsers()
      setOrgUsers(users)
    } catch {
      // silently fail — user can still see members, just can't add
    } finally {
      setUsersLoading(false)
    }
  }

  const handleAddMember = async (userId: number) => {
    setIsAdding(userId)
    try {
      await dispatch(addGroupMember({ groupId: group.id, userId })).unwrap()
      toast({
        title: t('groups.addMemberSuccess', { ns: 'admin' }),
        status: 'success',
        duration: 2000,
      })
    } catch {
      toast({ title: t('groups.addMemberFailed', { ns: 'admin' }), status: 'error', duration: 3000 })
    } finally {
      setIsAdding(null)
    }
  }

  const handleRemoveMember = async (userId: number) => {
    try {
      await dispatch(removeGroupMember({ groupId: group.id, userId })).unwrap()
      toast({
        title: t('groups.removeMemberSuccess', { ns: 'admin' }),
        status: 'success',
        duration: 2000,
      })
    } catch {
      toast({ title: t('groups.removeMemberFailed', { ns: 'admin' }), status: 'error', duration: 3000 })
    }
  }

  const memberUserIds = new Set(members.map((m) => m.user_id))

  const availableUsers = orgUsers.filter(
    (u) =>
      !memberUserIds.has(u.id) &&
      (u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{group.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Current members */}
            <Text fontWeight="semibold" fontSize="sm">
              {t('groups.members', { ns: 'admin' })}
            </Text>
            {isLoading ? (
              <Spinner size="sm" />
            ) : members.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                {t('groups.noMembersYet', { ns: 'admin' })}
              </Text>
            ) : (
              <VStack spacing={2} align="stretch">
                {members.map((member) => (
                  <HStack
                    key={member.id}
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg={rowBg}
                  >
                    <HStack spacing={2}>
                      <Avatar size="xs" name={member.user?.name} />
                      <Box>
                        <Text fontSize="sm" fontWeight="medium">
                          {member.user?.name || `User #${member.user_id}`}
                        </Text>
                        {member.user?.email && (
                          <Text fontSize="xs" color="gray.500">
                            {member.user.email}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                    <IconButton
                      aria-label={t('groups.removeMember', { ns: 'admin' })}
                      icon={<FiTrash2 />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleRemoveMember(member.user_id)}
                    />
                  </HStack>
                ))}
              </VStack>
            )}

            <Divider />

            {/* Available users to add */}
            <Text fontWeight="semibold" fontSize="sm">
              {t('groups.availableUsers', { ns: 'admin' })}
            </Text>

            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder={t('groups.searchUsers', { ns: 'admin' })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>

            {usersLoading ? (
              <Spinner size="sm" />
            ) : availableUsers.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                {t('groups.noUsersAvailable', { ns: 'admin' })}
              </Text>
            ) : (
              <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                {availableUsers.map((user) => (
                  <HStack
                    key={user.id}
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg={rowBg}
                  >
                    <HStack spacing={2}>
                      <Avatar size="xs" name={user.name} />
                      <Box>
                        <Text fontSize="sm" fontWeight="medium">
                          {user.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {user.email}
                        </Text>
                      </Box>
                    </HStack>
                    <IconButton
                      aria-label={t('groups.addMember', { ns: 'admin' })}
                      icon={isAdding === user.id ? <Spinner size="xs" /> : <FiUserPlus />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      isDisabled={isAdding !== null}
                      onClick={() => handleAddMember(user.id)}
                    />
                  </HStack>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('buttons.close', { ns: 'common' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
